<template>
  <div>
    <div class="d-flex flex-column mb-2">
      <div class="d-flex align-items-center mb-1">
        <h6 class="font-weight-bold mb-0 project-title">
          {{ $t('job-project.project') }}:
        </h6>
      </div>
      <div v-if="!loading && jobs.length > 0" class="project-financials-inline">
        <div class="financial-group">
          <div class="financial-row">
            <div class="financial-pair">
              <span class="financial-label-left">
                {{ $t('job-project.revenue') }}:
              </span>
              <span class="financial-value">{{ totalRevenue | currency }}</span>
            </div>
            <div class="financial-pair">
              <span class="financial-label-right ml-3" style="min-width: auto">
                {{ $t('job-project.contribution-margin-absolute') }}:
              </span>
              <span
                class="financial-value"
                :class="{
                  'text-danger': contributionMargin < 0,
                }"
              >
                {{ contributionMargin | currency }}
              </span>
            </div>
            <div class="financial-pair ml-3">
              <b-dropdown
                v-if="projectId"
                variant="outline-secondary"
                size="sm"
                :title="$t('job-project.gaeb-export')"
              >
                <template #button-content>
                  <i
                    class="material-icons"
                    style="font-size: 16px; vertical-align: middle"
                  >
                    download
                  </i>
                  GAEB
                </template>
                <b-dropdown-header>
                  {{ $t('gaeb.x84') }}
                </b-dropdown-header>
                <b-dropdown-item @click="exportProjectGaeb('customer')">
                  {{ $t('job-project.gaeb-export-customer') }}
                </b-dropdown-item>
                <b-dropdown-item @click="exportProjectGaeb('brand')">
                  {{ $t('job-project.gaeb-export-brand') }}
                </b-dropdown-item>
                <b-dropdown-item @click="exportProjectGaeb('partner')">
                  {{ $t('job-project.gaeb-export-partner') }}
                </b-dropdown-item>
                <b-dropdown-divider />
                <b-dropdown-item @click="exportProjectGaeb('customer', 'x83')">
                  {{ $t('gaeb.x83') }}
                </b-dropdown-item>
                <b-dropdown-item @click="exportProjectGaeb('customer', 'x81')">
                  {{ $t('gaeb.x81') }}
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
          <div class="financial-row">
            <div class="financial-pair">
              <span class="financial-label-left">
                {{ $t('job-project.costs') }}:
              </span>
              <span class="financial-value">{{ totalCosts | currency }}</span>
            </div>
            <div class="financial-pair">
              <span class="financial-label-right ml-3" style="min-width: auto">
                {{ $t('job-project.contribution-margin-percent') }}:
              </span>
              <span
                :class="{
                  'text-danger': contributionMarginPercent < 0,
                }"
                class="financial-value"
              >
                <template v-if="contributionMargin < -1">
                  {{ $t('negative-margin') }}
                </template>
                <template v-else>
                  {{ contributionMarginPercent | percentage }}
                </template>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="job-strip-container border rounded p-2">
      <div v-if="loading" class="spinner">
        <b-spinner large></b-spinner>
      </div>
      <div
        v-else
        v-for="item in jobs"
        :key="item.sfid"
        :class="[
          'job-box',
          'text-nowrap',
          'd-flex',
          'flex-column',
          'mr-3',
          { 'job-box-selected': current_job_id === item.sfid },
        ]"
      >
        <div
          :class="[
            'small',
            { 'font-weight-bold': current_job_id === item.sfid },
          ]"
        >
          {{ item.sequence_index }}. (
          <a
            :label="item.job.name"
            :href="`../jobs/${item.job.name}`"
            class="text-nowrap"
            @click="onJobLinkClick($event, item)"
          >
            {{ item.job.name }}
          </a>
          )
        </div>
        <div
          :class="[
            'job-status',
            'small',
            { 'font-weight-bold': current_job_id === item.sfid },
          ]"
        >
          {{
            item.status_description?.gid_name?.[locale] ||
            item.status_description?.gid_name?.['en'] ||
            item.status
          }}
        </div>
        <div class="job-type">
          <JobTypes :job="item" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import JobTypes from '@gid/vue-common/components/JobTypes.vue';

export default {
  props: {
    current_job_id: {
      type: String,
    },
    jobs: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: true,
    },
    projectId: {
      type: [Number, String],
      default: null,
    },
    accessToken: {
      type: String,
      default: '',
    },
  },
  components: {
    JobTypes,
  },
  computed: {
    ...mapGetters(['locale']),
    totalRevenue() {
      return this.jobs.reduce((sum, job) => sum + (job.revenue || 0), 0);
    },
    totalCosts() {
      return this.jobs.reduce((sum, job) => sum + (job.costs || 0), 0);
    },
    contributionMargin() {
      return this.totalRevenue - this.totalCosts;
    },
    contributionMarginPercent() {
      if (this.totalRevenue === 0) return 0;
      return Math.max(this.contributionMargin / this.totalRevenue, -1);
    },
  },
  methods: {
    onJobLinkClick(event, item) {
      // Let modifier / middle clicks fall through to the browser (real new tab);
      // a plain left click opens the job as a console tab in the jobs list.
      if (
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.button === 1
      ) {
        return;
      }
      event.preventDefault();
      this.$emit('open-job', item);
    },
    exportProjectGaeb(priceTarget = 'customer', format = 'x84') {
      const url = `/data-api/gaeb-export/project/${this.projectId}?priceTarget=${priceTarget}&format=${format}&jwt=${this.accessToken}`;
      window.open(url, '_blank');
    },
  },
};
</script>

<style scoped lang="scss">
.job-strip-container {
  display: flex;
  overflow-x: auto;
  white-space: nowrap;
  background: #f8f9fa;
}

.job-box {
  max-width: 160px;
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 0.75rem;
  flex-shrink: 0;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  justify-content: space-between;
  transition: border 0.2s ease-in-out;
}

.job-box-selected {
  border: 4px solid #c8cacc;
}

.job-type {
  position: relative;
  bottom: 0;
  margin-left: 20%;
  margin-top: 2px;
}

.spinner {
  margin-left: 50%;
}

.job-status {
  width: 100%;
  text-align: center;
  text-wrap-mode: wrap;
}

.project-financials-inline {
  margin-top: 2px;
  display: flex;
  flex-direction: column;
  font-size: 0.875rem;

  .financial-group {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }

  .financial-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .financial-label-right {
    color: #6c757d;
    min-width: 230px !important;
    font-size: 1rem;
    font-weight: 600;
  }

  .financial-label-left {
    color: #6c757d;
    min-width: 80px;
    font-size: 1rem;
    font-weight: 600;
  }

  .financial-value {
    font-weight: 600;
  }

  .financial-pair {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 200px;
  }
}
</style>
